import React, { useEffect, useState } from 'react'
import { SchoolInformationRequest } from 'infrastructure/api'
import {
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SchoolHeader from './SchoolHeader'
import SAMLForm from '../shared/SAMLForm'
import DeleteResourceButton from '../shared/DeleteResourceButton'
import { useNavigateTo } from 'infrastructure/routing'
import DataRemovalButton from '../shared/DataRemovalButton'
import { useSchoolResource } from '../../../api/school.hooks'
import ResyncDefaultChannelButton from '../shared/ResyncDefaultChannelButton'

export const SchoolEdit = ({ id }: { id: number | string }) => {
  const { data: schoolData, updateSchoolInformation } = useSchoolResource(id)
  const [error, setError] = useState('')
  const navigate = useNavigateTo()
  const [schoolInformation, setSchoolInformation] =
    useState<SchoolInformationRequest>()
  // Adding the state below because I just want to send the information that has been changed
  const [schoolInfoToBeUpdated, setSchoolInfoToBeUpdated] =
    useState<SchoolInformationRequest>()

  useEffect(() => {
    if (!schoolData) {
      return
    }
    setSchoolInformation({
      school_name: schoolData.name,
      school_address: schoolData.address,
      school_zip_code: schoolData.zip_code,
      school_city: schoolData.city,
      school_county: schoolData.county,
      school_state: schoolData.state,
      school_country: schoolData.country,
      school_is_public: schoolData.is_public,
      school_is_verified: schoolData.is_verified,
    })
  }, [schoolData])

  const onChangeValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError('')
    const { name, value } = e.target
    setSchoolInformation({ ...schoolInformation, [name]: value })
    setSchoolInfoToBeUpdated({ ...schoolInfoToBeUpdated, [name]: value })
  }

  const onChangeChecked = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError('')
    const { name, checked } = e.target
    setSchoolInformation({ ...schoolInformation, [name]: checked })
    setSchoolInfoToBeUpdated({ ...schoolInfoToBeUpdated, [name]: checked })
  }

  const onSubmit = async (e: React.FormEvent<EventTarget>) => {
    e.preventDefault()
    try {
      if (!schoolInformation) {
        return
      }
      await updateSchoolInformation(schoolInformation)
      navigate(`/schools/${id}/show`)
    } catch {
      setError('Error updating School. Please try again.')
    }
  }

  if (!schoolData) {
    return <div>Loading...</div>
  }
  return (
    <div className="content-color">
      <SchoolHeader data={schoolData} />
      <hr />
      <DataRemovalButton schoolID={schoolData.id} />
      <hr />
      <hr />
      <ResyncDefaultChannelButton schoolID={schoolData.id} />
      <hr />
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          Update school
        </AccordionSummary>
        <AccordionDetails>
          <Box component="form" sx={{ width: '70ch' }} onSubmit={onSubmit}>
            <Box m={2}>
              <TextField
                label="School Name"
                placeholder="School Name"
                name="school_name"
                variant="outlined"
                onChange={onChangeValue}
                value={schoolInformation?.school_name || ''}
                fullWidth
              />
            </Box>
            <Box m={2}>
              <TextField
                label="School Address"
                placeholder="School Address"
                name="school_address"
                variant="outlined"
                onChange={onChangeValue}
                value={schoolInformation?.school_address || ''}
                fullWidth
              />
            </Box>
            <Box m={2}>
              <TextField
                label="ZIP Code"
                placeholder="ZIP Code"
                name="school_zip_code"
                variant="outlined"
                onChange={onChangeValue}
                value={schoolInformation?.school_zip_code || ''}
                fullWidth
              />
            </Box>
            <Box m={2}>
              <TextField
                label="City"
                placeholder="City"
                name="school_city"
                variant="outlined"
                onChange={onChangeValue}
                value={schoolInformation?.school_city || ''}
                fullWidth
              />
            </Box>
            <Box m={2}>
              <TextField
                label="County"
                placeholder="County"
                name="school_county"
                variant="outlined"
                onChange={onChangeValue}
                value={schoolInformation?.school_county || ''}
                fullWidth
              />
            </Box>
            <Box m={2}>
              <TextField
                label="State"
                placeholder="State"
                name="school_state"
                variant="outlined"
                onChange={onChangeValue}
                value={schoolInformation?.school_state || ''}
                fullWidth
              />
            </Box>
            <Box m={2}>
              <TextField
                label="Country"
                placeholder="Country"
                name="school_country"
                variant="outlined"
                onChange={onChangeValue}
                value={schoolInformation?.school_country || ''}
                fullWidth
              />
            </Box>
            <Box m={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={schoolInformation?.school_is_public || false}
                    onChange={onChangeChecked}
                    name="school_is_public"
                  />
                }
                label="Is Public"
              />
            </Box>
            <Box m={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={schoolInformation?.school_is_verified || false}
                    onChange={onChangeChecked}
                    name="school_is_verified"
                  />
                }
                label="Is Verified"
              />
            </Box>
            <Box m={2}>
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                fullWidth
              >
                Save
              </Button>{' '}
            </Box>
          </Box>
          {error && (
            <p style={{ color: 'red', textAlign: 'center' }}> {error} </p>
          )}
        </AccordionDetails>
      </Accordion>
      <SAMLForm districtID={-1} schoolID={Number(schoolData.id)} />
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          Delete School
        </AccordionSummary>
        <AccordionDetails>
          <DeleteResourceButton
            name={schoolData.name}
            resource="School"
            schoolID={schoolData.id}
            districtID={schoolData?.district?.id}
          />
        </AccordionDetails>
      </Accordion>
    </div>
  )
}
